/**
 * "Who is this line charged to?" — one model for every accounting surface.
 *
 * Replaces the old two-step control (an `Advanced` on/off switch that revealed a
 * `ChargeTo` button group). Turning `Advanced` off was the only way to say "the company
 * pays", and it was never stated — only implied by the absence of a choice. Company is
 * now an option like any other.
 *
 * `ChargeTo` is **UI state only**. It is not a column on tbl_ExpenseLine, tbl_InvoiceLine
 * or tbl_RecurringExpenseLine, and no backend code reads it — the server reads
 * ChargeToContactID / ChargeToOrganizationID / ChargeToTruckID directly. Those three FKs
 * are the contract; this discriminator only decides which of them a line carries.
 *
 * Deliberately free of app imports, like vendor-match.js and cat-scale-import.js: pulling
 * in FieldsManager would drag the localStorage/util-helpers/util-constants cycle in and
 * make the file throw at import time under vitest. So it returns plain descriptors and
 * each caller applies them.
 */

// Values 1-3 are unchanged from the old ChargeTo group on purpose: a call site that still
// compares `=== 2` keeps meaning Organization. Company is new, and 0 also sorts first —
// ButtonsGroup renders integer-like keys in ascending order.
export const CHARGE_TO_COMPANY = 0;
export const CHARGE_TO_CONTACT = 1;
export const CHARGE_TO_ORGANIZATION = 2;
export const CHARGE_TO_TRUCK = 3;

/** The FK each option fills in. Company fills none — that is what makes it "the company". */
export const CHARGE_TO_FIELD = {
    [CHARGE_TO_CONTACT]: 'ChargeToContactID',
    [CHARGE_TO_ORGANIZATION]: 'ChargeToOrganizationID',
    [CHARGE_TO_TRUCK]: 'ChargeToTruckID',
};

export const CHARGE_TO_FIELDS = Object.values(CHARGE_TO_FIELD);

/**
 * Button-group `data` for a surface.
 *
 * Options follow what the line can actually store: only tbl_ExpenseLine has
 * ChargeToTruckID, so invoice and recurring lines leave Truck out rather than offering a
 * choice that would be dropped on save.
 */
export function chargeToOptions(translate, {allowTruck = false, allowOrganization = true} = {}) {
    const options = {
        [CHARGE_TO_COMPANY]: translate('label.ChargeToCompany'),
        [CHARGE_TO_CONTACT]: translate('label.ChargeToContactID'),
    };

    if (allowOrganization) {
        options[CHARGE_TO_ORGANIZATION] = translate('label.ChargeToOrganizationID');
    }
    if (allowTruck) {
        options[CHARGE_TO_TRUCK] = translate('label.ChargeToTruckID');
    }

    return options;
}

/**
 * Which option a stored line is already on.
 *
 * Reads the FKs rather than any saved discriminator, because there is none. Replaces
 * `item.ChargeToOrganizationID ? 2 : 1`, which reopened a truck-charged line as Contact
 * and an uncharged line as Contact too.
 */
export function chargeToFromItem(item) {
    if (item?.ChargeToTruckID) {
        return CHARGE_TO_TRUCK;
    }
    if (item?.ChargeToOrganizationID) {
        return CHARGE_TO_ORGANIZATION;
    }
    if (item?.ChargeToContactID) {
        return CHARGE_TO_CONTACT;
    }

    return CHARGE_TO_COMPANY;
}

/**
 * How each charge-to field should render for the selected option: the one the option
 * points at is a select-search, the rest are hidden.
 *
 * `requireTarget` makes the visible one mandatory — used where a line charged to somebody
 * must actually name them.
 *
 * @returns {Object<string, {type: string, validate: string[]}>}
 */
export function chargeToFieldState(chargeTo, {requireTarget = true} = {}) {
    const target = CHARGE_TO_FIELD[Number(chargeTo)];

    return CHARGE_TO_FIELDS.reduce((memo, name) => {
        const isTarget = name === target;

        memo[name] = {
            type: isTarget ? 'select-search' : 'hidden',
            validate: isTarget && requireTarget ? ['empty'] : [''],
        };

        return memo;
    }, {});
}

/**
 * The charge-to FKs to blank out for the selected option — every one the option does not
 * point at. Switching Contact -> Truck must not leave the old contact behind, and Company
 * clears all three.
 *
 * @returns {Object<string, string>} field name -> '' , ready to feed FieldsManager
 */
export function chargeToClearedValues(chargeTo) {
    const target = CHARGE_TO_FIELD[Number(chargeTo)];

    return CHARGE_TO_FIELDS.reduce((memo, name) => {
        if (name !== target) {
            memo[name] = '';
        }

        return memo;
    }, {});
}

/** True when the line is charged to somebody other than the company. */
export function isChargedToSomeone(chargeTo) {
    return Number(chargeTo) !== CHARGE_TO_COMPANY;
}
